import { AttributesMixin, Collection, Model, attributes, define, type } from "@type-r/models";
import { matchString } from "api/common";
import { asTimelessDate } from "lqx-react/lib";
import { AssetChange } from "./assetChange";
import { AssetData, AssetType as AssetObject } from "./assetData";
import { AssetEvent } from "./assetEvent";
import { AssetType } from "./assetType";
import { CompanyItem } from "./company";
import { CountsModel } from "./countsModel";
import { restfulIO } from "./endpoint";

export interface CounterParty extends AttributesMixin<typeof CounterParty> { }
export const CounterParty = attributes({
    company: CompanyItem
});

export interface UnappliedCash extends AttributesMixin<typeof UnappliedCash> { }
@define
export class UnappliedCash extends AssetData {

    static attributes = {
        ...AssetData.attributes,
        assetType: type(AssetObject).value({ id: 11, name: 'Unapplied Cash' }),
        payor: CounterParty,
        payee: CounterParty,
        currency: String,
        amount: type(Number).value(0),
        referenceNumber: String,
        effectiveDate: type(Date).null
    }

    onLoaded() { }

    get isDigitized() {
        return this.state.name == 'Digitized';
    }
}

export interface ManualUnappliedCash extends AttributesMixin<typeof ManualUnappliedCash> { }
@define
export class ManualUnappliedCash extends Model {
    static endpoint = restfulIO('api/v1/ingest/unappliedcashes');

    static attributes = {
        id: String,
        status: String,
        ...UnappliedCash.attributes
    }
}

export interface UnappliedCashChange extends AttributesMixin<typeof UnappliedCashChange> { }
@define
export class UnappliedCashChange extends AssetChange {
    static endpoint = restfulIO('api/v1/ingest/unappliedcashes/assets');

    static attributes = {
        ...AssetChange.attributes,
        updateAsset: UnappliedCash,
        assetType: type(Number).value(AssetType.unappliedCash),
        assetEvents: type([AssetEvent]).value([]).dontSave
    };

    getAssetType() {
        return AssetType.unappliedCash;
    }

    getAssetName() {
        return 'Unapplied Cash';
    }

    get payor(): CounterParty {
        return this.getValue("payor") || new CounterParty();
    }

    get payee(): CounterParty {
        return this.getValue("payee") || new CounterParty();
    }

    get amount(): String {
        return this.getValue("amount");
    }

    get effectiveDate(): Date {
        const value = this.getValue("effectiveDate");
        return value ? new Date(value) : null;
    }

    get referenceNumber(): String {
        return this.getValue("referenceNumber");
    }

    filter(filter) {
        return (matchString(this.referenceNumber.toString(), filter.search) ||
            matchString(this.payor.company.name, filter.search) ||
            matchString(this.amount.toString(), filter.search) ||
            matchString(asTimelessDate(this.effectiveDate), filter.search) ||
            matchString(this.state.name, filter.search) ||
            matchString(this.payee.company.name, filter.search)) &&
            this.hasReceivedAssetEventsFor(filter.eventType);
    }
}

export interface UnappliedCashData extends AttributesMixin<typeof UnappliedCashData> { }
@define
export class UnappliedCashData extends Model {
    static endpoint = restfulIO('api/v1/ingest/unappliedcashes/assets');

    static attributes = {
        count: Number,
        items: [UnappliedCashChange]
    };
}

export interface UnappliedCashCount extends AttributesMixin<typeof UnappliedCashCount> { }
@define
export class UnappliedCashCount extends CountsModel {
    static endpoint = restfulIO('api/v1/ingest/unappliedcashes/counts');

    static attributes = {
        ...CountsModel.attributes
    };
}


export interface UnappliedCashVaultCard extends AttributesMixin<typeof UnappliedCashVaultCard> { }
@define
export class UnappliedCashVaultCard extends Model {

    static idAttribute = "id";

    static attributes = {
        stateId: Number,
        createdOn: type(Date),
        uid: String,
        dataset: type(UnappliedCash).dontSave,
        hashKey: String,
        updatedOn: type(Date),
        eTag: String,
        assetTypeId: Number,
        dataSourceId: Number,
        platformCode: String,
        createdBy: String
    };
}

@define
export class UnappliedCashVaultCardFilters extends Model {
    static attributes = {
        state: type(Number).value(0)
    }
}

export interface UnappliedCashVaultPage extends AttributesMixin<typeof UnappliedCashVaultPage> { }
@define
export class UnappliedCashVaultPage extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/Assets');
    static idAttribute = "assetId";


    static attributes = {
        assetId: String,

        filter: type(UnappliedCashVaultCardFilters),
        sortBy: {
            name: 'createdOn',
            isAsc: false
        },

        unappliedCashes: type({ items: [UnappliedCashVaultCard], count: Number }).endpoint(restfulIO('./')).dontSave,
        selected: Collection.ofRefs(UnappliedCashVaultCard)
    }
}